/**
 * CreatorPromoPage — operator UI for `/api/v1/creator-promo`.
 *
 * Three panels stacked in a single column:
 *   1. Create form — kind radio (exclusive|general|unique) + code +
 *      influencer_id + max_uses int + discount_bps. Unique forces
 *      max_uses=1 and disables the field.
 *   2. Codes table — GET /by-influencer/:id, renders kind badge, code,
 *      used/max counter, "disabled" badge when at cap or inactive, and
 *      a per-row redeem button (admin-only server-side; the button is
 *      always rendered client-side so the operator sees the control).
 *   3. Validate input — enter a code, click validate → shows valid or
 *      invalid + reason from the backend.
 *
 * Data: TanStack Query, Sonner toasts, zero extra deps.
 */

import { useMemo, useReducer, useState } from "react";
import {
	keepPreviousData,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import { ApiError } from "@/api-client";
import {
	createPromo,
	creatorPromoQueryKeys,
	EMPTY_FORM,
	formReducer,
	isAtCap,
	isDisabled as rowIsDisabled,
	isMaxUsesLocked,
	listPromosByInfluencer,
	PROMO_KIND_DESCRIPTIONS,
	PROMO_KIND_LABELS,
	PROMO_KINDS,
	reasonCopy,
	redeemPromo,
	validateFormState,
	validatePromo,
	type CreatePromoArgs,
	type CreatorPromoCodeView,
	type PromoKind,
	type ValidateArgs,
	type ValidateResponse,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens — kept local so the file stays self-contained.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:bg-zinc-100 disabled:text-zinc-500";
const HINT = "text-xs text-zinc-500";
const ERROR_TEXT = "text-xs text-red-600";
const BTN_PRIMARY =
	"rounded-[6px] border border-emerald-600 bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_SECONDARY =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";

export interface CreatorPromoPageProps {
	/**
	 * When provided, prefills the "codes table" influencer filter + the
	 * create-form influencer id. Used for deep-linking from creator
	 * detail pages.
	 */
	initialInfluencerId?: string;
}

// ---------------------------------------------------------------------------
// Page — composition only. Keep logic in the sub-components / api module.
// ---------------------------------------------------------------------------

export function CreatorPromoPage({ initialInfluencerId }: CreatorPromoPageProps = {}) {
	const [tableInfluencerId, setTableInfluencerId] = useState<string>(
		initialInfluencerId?.trim() ?? "",
	);

	return (
		<main className="mx-auto flex w-full max-w-3xl flex-col gap-8 px-6 py-10 sm:py-16">
			<Breadcrumb />
			<Heading />
			<CreateForm
				initialInfluencerId={initialInfluencerId}
				onCreated={(row) => setTableInfluencerId(row.influencer_id)}
			/>
			<ValidatePanel />
			<CodesTable
				influencerId={tableInfluencerId}
				onInfluencerIdChange={setTableInfluencerId}
			/>
			<DocHint />
		</main>
	);
}

export default CreatorPromoPage;

// ---------------------------------------------------------------------------
// Header fragments — presentational only.
// ---------------------------------------------------------------------------

function Breadcrumb() {
	return (
		<nav aria-label="Breadcrumb" className="text-sm text-zinc-500">
			<ol className="flex flex-wrap items-center gap-1">
				<li>
					<a
						href="/"
						className="rounded-[6px] px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
					>
						Dashboard
					</a>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li>
					<span className="rounded-[6px] px-1 py-0.5">Creators</span>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li className="text-zinc-900">Promo codes</li>
			</ol>
		</nav>
	);
}

function Heading() {
	return (
		<header className="flex flex-col gap-2">
			<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
				Creators
			</p>
			<h1 className="text-brand-gradient text-3xl font-semibold tracking-tight sm:text-4xl">
				Creator promo codes
			</h1>
			<p className="max-w-prose text-sm text-zinc-600">
				Issue exclusive, general, or unique promo codes to influencers.
				Exclusive codes lock to one creator. General codes spread across
				your workspace. Unique codes burn on first use.
			</p>
		</header>
	);
}

function DocHint() {
	return (
		<aside className="rounded-[8px] border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-600">
			<p className="font-medium text-zinc-800">How it works</p>
			<ul className="mt-2 flex list-inside list-disc flex-col gap-1">
				<li>Exclusive codes only validate for the issued influencer.</li>
				<li>
					General codes accept any influencer inside the current workspace.
				</li>
				<li>
					Unique codes are globally single-use and cannot be re-issued once
					redeemed.
				</li>
			</ul>
		</aside>
	);
}

// ---------------------------------------------------------------------------
// CreateForm — POST /api/v1/creator-promo.
// ---------------------------------------------------------------------------

interface CreateFormProps {
	initialInfluencerId?: string;
	onCreated: (row: CreatorPromoCodeView) => void;
}

export function CreateForm({ initialInfluencerId, onCreated }: CreateFormProps) {
	const queryClient = useQueryClient();
	const [state, dispatch] = useReducer(formReducer, {
		...EMPTY_FORM,
		influencerId: initialInfluencerId?.trim() ?? "",
	});
	const [errors, setErrors] = useState<Record<string, string>>({});

	const mutation = useMutation({
		mutationFn: (args: CreatePromoArgs) => createPromo(args),
		onSuccess: (row) => {
			toast.success("Promo code created.");
			queryClient.invalidateQueries({
				queryKey: creatorPromoQueryKeys.byInfluencer(row.influencer_id),
			});
			onCreated(row);
			dispatch({ type: "reset" });
			if (initialInfluencerId) {
				dispatch({
					type: "set_influencer_id",
					influencerId: initialInfluencerId,
				});
			}
		},
		onError: (err: Error) => {
			if (err instanceof ApiError) {
				toast.error(err.message);
			} else {
				toast.error(err.message || "Unable to create promo code.");
			}
		},
	});

	const maxUsesLocked = isMaxUsesLocked(state.kind);

	function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const parsed = validateFormState(state);
		if (!parsed.success) {
			setErrors(parsed.errors);
			toast.error("Please fix the highlighted fields.");
			return;
		}
		setErrors({});
		mutation.mutate({
			kind: parsed.data.kind,
			code: parsed.data.code,
			influencerId: parsed.data.influencerId,
			maxUses: parsed.data.maxUses,
			discountBps: parsed.data.discountBps,
			active: parsed.data.active,
		});
	}

	return (
		<form
			onSubmit={handleSubmit}
			className={`${CARD} flex flex-col gap-6 p-6`}
			noValidate
			aria-label="Create creator promo code"
		>
			<header className="flex flex-col gap-1">
				<h2 className="text-lg font-semibold text-zinc-900">Create code</h2>
				<p className={HINT}>
					Admin-only. The server rejects duplicates inside the tenant for
					exclusive/general kinds, and globally for unique kinds.
				</p>
			</header>

			<KindField kind={state.kind} onChange={(k) => dispatch({ type: "set_kind", kind: k })} />

			<div className="grid gap-4 sm:grid-cols-2">
				<CodeField
					value={state.code}
					onChange={(v) => {
						dispatch({ type: "set_code", code: v });
						if (errors.code) setErrors(({ code: _c, ...rest }) => rest);
					}}
					error={errors.code}
				/>
				<InfluencerField
					value={state.influencerId}
					kind={state.kind}
					onChange={(v) => {
						dispatch({ type: "set_influencer_id", influencerId: v });
						if (errors.influencerId)
							setErrors(({ influencerId: _i, ...rest }) => rest);
					}}
					error={errors.influencerId}
					locked={Boolean(initialInfluencerId)}
				/>
			</div>

			<div className="grid gap-4 sm:grid-cols-2">
				<MaxUsesField
					value={state.maxUses}
					locked={maxUsesLocked}
					onChange={(v) => {
						dispatch({ type: "set_max_uses", maxUses: v });
						if (errors.maxUses)
							setErrors(({ maxUses: _m, ...rest }) => rest);
					}}
					error={errors.maxUses}
				/>
				<DiscountBpsField
					value={state.discountBps}
					onChange={(v) => {
						dispatch({ type: "set_discount_bps", discountBps: v });
						if (errors.discountBps)
							setErrors(({ discountBps: _d, ...rest }) => rest);
					}}
					error={errors.discountBps}
				/>
			</div>

			<Footer
				busy={mutation.isPending}
				onReset={() => {
					dispatch({ type: "reset" });
					setErrors({});
				}}
			/>
		</form>
	);
}

// ---------------------------------------------------------------------------
// Field components.
// ---------------------------------------------------------------------------

function KindField(props: { kind: PromoKind; onChange: (k: PromoKind) => void }) {
	return (
		<fieldset className="flex flex-col gap-2">
			<legend className={LABEL}>Kind</legend>
			<div
				role="radiogroup"
				aria-label="Promo code kind"
				className="flex flex-col gap-2 sm:flex-row"
			>
				{PROMO_KINDS.map((k) => (
					<KindRadio
						key={k}
						kind={k}
						active={props.kind === k}
						onSelect={() => props.onChange(k)}
					/>
				))}
			</div>
			<p className={HINT}>{PROMO_KIND_DESCRIPTIONS[props.kind]}</p>
		</fieldset>
	);
}

function KindRadio(props: { kind: PromoKind; active: boolean; onSelect: () => void }) {
	const base =
		"flex-1 rounded-[6px] border px-3 py-2 text-sm font-medium text-left transition-colors";
	const on = "border-emerald-500 bg-emerald-50 text-emerald-700";
	const off =
		"border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300 hover:bg-zinc-50";
	return (
		<button
			type="button"
			role="radio"
			aria-checked={props.active}
			onClick={props.onSelect}
			className={`${base} ${props.active ? on : off}`}
			data-kind={props.kind}
		>
			<span className="flex items-center gap-2">
				<span
					aria-hidden="true"
					className={`h-1.5 w-1.5 rounded-full ${
						props.active ? "bg-emerald-500" : "bg-zinc-300"
					}`}
				/>
				{PROMO_KIND_LABELS[props.kind]}
			</span>
		</button>
	);
}

function CodeField(props: { value: string; onChange: (v: string) => void; error?: string }) {
	return (
		<div className="flex flex-col gap-1.5">
			<label htmlFor="creator-promo-code" className={LABEL}>
				Code
			</label>
			<input
				id="creator-promo-code"
				name="code"
				type="text"
				autoComplete="off"
				placeholder="e.g. SUMMER-25"
				className={INPUT}
				value={props.value}
				onChange={(e) => props.onChange(e.currentTarget.value)}
				aria-invalid={Boolean(props.error)}
				aria-describedby={props.error ? "creator-promo-code-error" : undefined}
				maxLength={64}
			/>
			<p className={HINT}>Letters, digits, dash, underscore. 3–64 chars.</p>
			{props.error && (
				<p id="creator-promo-code-error" className={ERROR_TEXT}>
					{props.error}
				</p>
			)}
		</div>
	);
}

function InfluencerField(props: {
	value: string;
	kind: PromoKind;
	onChange: (v: string) => void;
	error?: string;
	locked?: boolean;
}) {
	const required = props.kind === "exclusive";
	return (
		<div className="flex flex-col gap-1.5">
			<label htmlFor="creator-promo-influencer" className={LABEL}>
				Influencer ID{" "}
				<span className="font-normal text-zinc-400">
					({required ? "required" : props.kind === "unique" ? "author, optional" : "optional"})
				</span>
			</label>
			<input
				id="creator-promo-influencer"
				name="influencerId"
				type="text"
				autoComplete="off"
				placeholder="e.g. inf_01h5x"
				className={INPUT}
				value={props.value}
				onChange={(e) => props.onChange(e.currentTarget.value)}
				disabled={props.locked}
				aria-invalid={Boolean(props.error)}
				aria-describedby={
					props.error ? "creator-promo-influencer-error" : undefined
				}
			/>
			{props.error && (
				<p id="creator-promo-influencer-error" className={ERROR_TEXT}>
					{props.error}
				</p>
			)}
		</div>
	);
}

function MaxUsesField(props: {
	value: string;
	locked: boolean;
	onChange: (v: string) => void;
	error?: string;
}) {
	return (
		<div className="flex flex-col gap-1.5">
			<label htmlFor="creator-promo-max-uses" className={LABEL}>
				Max uses{" "}
				{props.locked && (
					<span className="font-normal text-zinc-400">(unique = 1)</span>
				)}
			</label>
			<input
				id="creator-promo-max-uses"
				name="maxUses"
				type="number"
				min={1}
				step={1}
				inputMode="numeric"
				autoComplete="off"
				placeholder="Leave empty for unlimited"
				className={INPUT}
				value={props.value}
				onChange={(e) => props.onChange(e.currentTarget.value)}
				disabled={props.locked}
				aria-invalid={Boolean(props.error)}
				aria-describedby={
					props.error ? "creator-promo-max-uses-error" : undefined
				}
				data-locked={props.locked ? "true" : "false"}
			/>
			<p className={HINT}>
				{props.locked
					? "Unique codes burn on first redemption — max uses is locked to 1."
					: "Optional positive integer. Leave empty for an unlimited code."}
			</p>
			{props.error && (
				<p id="creator-promo-max-uses-error" className={ERROR_TEXT}>
					{props.error}
				</p>
			)}
		</div>
	);
}

function DiscountBpsField(props: {
	value: string;
	onChange: (v: string) => void;
	error?: string;
}) {
	return (
		<div className="flex flex-col gap-1.5">
			<label htmlFor="creator-promo-discount" className={LABEL}>
				Discount (bps){" "}
				<span className="font-normal text-zinc-400">(optional)</span>
			</label>
			<input
				id="creator-promo-discount"
				name="discountBps"
				type="number"
				min={0}
				max={10000}
				step={1}
				inputMode="numeric"
				autoComplete="off"
				placeholder="e.g. 1500"
				className={INPUT}
				value={props.value}
				onChange={(e) => props.onChange(e.currentTarget.value)}
				aria-invalid={Boolean(props.error)}
				aria-describedby={
					props.error ? "creator-promo-discount-error" : undefined
				}
			/>
			<p className={HINT}>Basis points. 10000 bps equals 100 percent.</p>
			{props.error && (
				<p id="creator-promo-discount-error" className={ERROR_TEXT}>
					{props.error}
				</p>
			)}
		</div>
	);
}

function Footer(props: { busy: boolean; onReset: () => void }) {
	return (
		<div className="flex items-center justify-end gap-2 border-t border-zinc-200 pt-4">
			<button
				type="button"
				onClick={props.onReset}
				disabled={props.busy}
				className={BTN_SECONDARY}
			>
				Reset
			</button>
			<button type="submit" disabled={props.busy} className={BTN_PRIMARY}>
				{props.busy ? "Creating." : "Create code"}
			</button>
		</div>
	);
}

// ---------------------------------------------------------------------------
// CodesTable — GET /by-influencer/:id.
// ---------------------------------------------------------------------------

interface CodesTableProps {
	influencerId: string;
	onInfluencerIdChange: (v: string) => void;
}

export function CodesTable({ influencerId, onInfluencerIdChange }: CodesTableProps) {
	const trimmed = influencerId.trim();
	const enabled = trimmed.length > 0;
	const queryClient = useQueryClient();

	const query = useQuery({
		queryKey: creatorPromoQueryKeys.byInfluencer(trimmed),
		queryFn: () => listPromosByInfluencer(trimmed),
		enabled,
		placeholderData: keepPreviousData,
	});

	const redeem = useMutation({
		mutationFn: (args: ValidateArgs) => redeemPromo(args),
		onSuccess: (res, vars) => {
			if (res.redeemed) {
				toast.success("Promo redeemed.");
			} else {
				toast.error(reasonCopy(res.reason));
			}
			queryClient.invalidateQueries({
				queryKey: creatorPromoQueryKeys.byInfluencer(vars.influencerId),
			});
		},
		onError: (err: Error) => {
			toast.error(err.message || "Redeem failed.");
		},
	});

	const rows = query.data ?? [];

	return (
		<section className={`${CARD} flex flex-col gap-4 p-6`} aria-label="Codes by influencer">
			<header className="flex flex-col gap-1">
				<h2 className="text-lg font-semibold text-zinc-900">Codes by influencer</h2>
				<p className={HINT}>
					Paste an influencer ID to list their promo codes in this workspace.
				</p>
			</header>

			<div className="flex flex-col gap-1.5">
				<label htmlFor="creator-promo-table-filter" className={LABEL}>
					Influencer ID
				</label>
				<input
					id="creator-promo-table-filter"
					type="text"
					autoComplete="off"
					placeholder="e.g. inf_01h5x"
					className={INPUT}
					value={influencerId}
					onChange={(e) => onInfluencerIdChange(e.currentTarget.value)}
				/>
			</div>

			{!enabled && (
				<p className={HINT} role="status">
					Enter an influencer ID above to load codes.
				</p>
			)}

			{enabled && query.isPending && (
				<p className={HINT} role="status" aria-live="polite">
					Loading codes.
				</p>
			)}

			{enabled && query.isError && (
				<p className={ERROR_TEXT} role="alert">
					{(query.error as Error)?.message ?? "Failed to load codes."}
				</p>
			)}

			{enabled && query.isSuccess && rows.length === 0 && (
				<p className={HINT} role="status">
					No codes issued to this influencer yet.
				</p>
			)}

			{enabled && query.isSuccess && rows.length > 0 && (
				<CodesTableBody
					rows={rows}
					busyId={redeem.isPending ? (redeem.variables?.code ?? null) : null}
					onRedeem={(row) =>
						redeem.mutate({
							code: row.code,
							influencerId: row.influencer_id,
						})
					}
				/>
			)}
		</section>
	);
}

function CodesTableBody(props: {
	rows: CreatorPromoCodeView[];
	busyId: string | null;
	onRedeem: (row: CreatorPromoCodeView) => void;
}) {
	return (
		<div className="overflow-x-auto">
			<table className="w-full border-collapse text-sm">
				<thead>
					<tr className="text-left text-xs uppercase tracking-[0.08em] text-zinc-500">
						<th className="border-b border-zinc-200 px-3 py-2 font-medium">Kind</th>
						<th className="border-b border-zinc-200 px-3 py-2 font-medium">Code</th>
						<th className="border-b border-zinc-200 px-3 py-2 font-medium">Used / max</th>
						<th className="border-b border-zinc-200 px-3 py-2 font-medium">Status</th>
						<th className="border-b border-zinc-200 px-3 py-2 font-medium sr-only">Actions</th>
					</tr>
				</thead>
				<tbody>
					{props.rows.map((row) => (
						<CodeRow
							key={row.id}
							row={row}
							busy={props.busyId === row.code}
							onRedeem={() => props.onRedeem(row)}
						/>
					))}
				</tbody>
			</table>
		</div>
	);
}

function CodeRow(props: {
	row: CreatorPromoCodeView;
	busy: boolean;
	onRedeem: () => void;
}) {
	const disabled = rowIsDisabled(props.row);
	const atCap = isAtCap(props.row);
	const used =
		props.row.max_uses === null
			? `${props.row.used_count} / \u221e`
			: `${props.row.used_count} / ${props.row.max_uses}`;
	return (
		<tr className="align-middle">
			<td className="border-b border-zinc-100 px-3 py-2">
				<KindBadge kind={props.row.kind} />
			</td>
			<td className="border-b border-zinc-100 px-3 py-2 font-mono text-xs text-zinc-800">
				{props.row.code}
			</td>
			<td className="border-b border-zinc-100 px-3 py-2 tabular-nums text-zinc-700">
				{used}
			</td>
			<td className="border-b border-zinc-100 px-3 py-2">
				{disabled ? <DisabledBadge atCap={atCap} active={props.row.active} /> : <ActiveBadge />}
			</td>
			<td className="border-b border-zinc-100 px-3 py-2 text-right">
				<button
					type="button"
					onClick={props.onRedeem}
					disabled={disabled || props.busy}
					className={BTN_SECONDARY}
					aria-label={`Redeem ${props.row.code}`}
				>
					{props.busy ? "Redeeming." : "Redeem"}
				</button>
			</td>
		</tr>
	);
}

function KindBadge(props: { kind: PromoKind }) {
	const base =
		"inline-flex items-center rounded-[6px] border px-2 py-0.5 text-xs font-medium";
	const styles: Record<PromoKind, string> = {
		exclusive: "border-emerald-200 bg-emerald-50 text-emerald-700",
		general: "border-sky-200 bg-sky-50 text-sky-700",
		unique: "border-zinc-300 bg-zinc-100 text-zinc-700",
	};
	return (
		<span className={`${base} ${styles[props.kind]}`}>
			{PROMO_KIND_LABELS[props.kind]}
		</span>
	);
}

function ActiveBadge() {
	return (
		<span className="inline-flex items-center rounded-[6px] border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
			Active
		</span>
	);
}

function DisabledBadge(props: { atCap: boolean; active: boolean }) {
	const label = !props.active
		? "Inactive"
		: props.atCap
			? "At cap"
			: "Disabled";
	return (
		<span className="inline-flex items-center rounded-[6px] border border-zinc-300 bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-600">
			{label}
		</span>
	);
}

// ---------------------------------------------------------------------------
// ValidatePanel — POST /api/v1/creator-promo/validate.
// ---------------------------------------------------------------------------

export function ValidatePanel() {
	const [code, setCode] = useState("");
	const [influencerId, setInfluencerId] = useState("");
	const [result, setResult] = useState<ValidateResponse | null>(null);
	const [error, setError] = useState<string | null>(null);

	const mutation = useMutation({
		mutationFn: (args: ValidateArgs) => validatePromo(args),
		onSuccess: (res) => {
			setResult(res);
			setError(null);
		},
		onError: (err: Error) => {
			setResult(null);
			setError(err.message || "Validation call failed.");
		},
	});

	const disabled = code.trim().length === 0 || influencerId.trim().length === 0;

	const resultBlock = useMemo(() => {
		if (error) {
			return (
				<p className={ERROR_TEXT} role="alert" data-testid="validate-result">
					{error}
				</p>
			);
		}
		if (!result) return null;
		if (result.valid) {
			return (
				<p
					className="text-sm font-medium text-emerald-700"
					role="status"
					aria-live="polite"
					data-testid="validate-result"
				>
					Valid code.
				</p>
			);
		}
		return (
			<p
				className="text-sm font-medium text-red-600"
				role="alert"
				data-testid="validate-result"
			>
				Invalid: {reasonCopy(result.reason)}
			</p>
		);
	}, [error, result]);

	return (
		<section
			className={`${CARD} flex flex-col gap-4 p-6`}
			aria-label="Validate promo code"
		>
			<header className="flex flex-col gap-1">
				<h2 className="text-lg font-semibold text-zinc-900">Validate code</h2>
				<p className={HINT}>
					Check a code without redeeming. Reason surfaces any issue such as
					invalid_influencer, max_uses_reached, or inactive.
				</p>
			</header>

			<form
				className="flex flex-col gap-4"
				onSubmit={(event) => {
					event.preventDefault();
					mutation.mutate({
						code: code.trim(),
						influencerId: influencerId.trim(),
					});
				}}
			>
				<div className="grid gap-4 sm:grid-cols-2">
					<div className="flex flex-col gap-1.5">
						<label htmlFor="creator-promo-validate-code" className={LABEL}>
							Code
						</label>
						<input
							id="creator-promo-validate-code"
							type="text"
							autoComplete="off"
							className={INPUT}
							placeholder="e.g. SUMMER-25"
							value={code}
							onChange={(e) => setCode(e.currentTarget.value)}
						/>
					</div>
					<div className="flex flex-col gap-1.5">
						<label
							htmlFor="creator-promo-validate-influencer"
							className={LABEL}
						>
							Influencer ID
						</label>
						<input
							id="creator-promo-validate-influencer"
							type="text"
							autoComplete="off"
							className={INPUT}
							placeholder="e.g. inf_01h5x"
							value={influencerId}
							onChange={(e) => setInfluencerId(e.currentTarget.value)}
						/>
					</div>
				</div>

				<div className="flex items-center justify-between gap-3">
					{resultBlock ?? <span className={HINT}>Awaiting input.</span>}
					<button
						type="submit"
						disabled={disabled || mutation.isPending}
						className={BTN_PRIMARY}
					>
						{mutation.isPending ? "Validating." : "Validate"}
					</button>
				</div>
			</form>
		</section>
	);
}
